import { t } from "@lingui/macro";
import {
  BankDocumentTypeEnum,
  ModelLockLockableTypeEnum,
  OutgoingInvoiceDetailDocument,
  type OutgoingInvoiceDetailQuery,
  type OutgoingInvoiceDetailQueryVariables,
  ProformaInvoiceDetailDocument,
  type ProformaInvoiceDetailQuery,
  type ProformaInvoiceDetailQueryVariables,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { appStore } from "@src/stores/AppStore";
import { can } from "@src/utils/components/permissions";
import { percent } from "@src/utils/formatters";
import { PercentModel } from "@src/utils/models";
import { action, computed, makeObservable, observable } from "mobx";
import { Invoice, type InvoiceData } from "../models";
import { InvoiceTypeEnum } from ".";

export type InvoiceItemVatGroup = {
  vatPercent: number;
  vatPercentFormatted: string;
  vatAmount: number;
};

export class InvoiceDetailStore {
  @observable readonly type: InvoiceTypeEnum;
  @observable.ref invoice?: Invoice;
  @observable.ref hasFetched = false;

  private outgoingFetcher = new FetchHelper<
    OutgoingInvoiceDetailQuery,
    OutgoingInvoiceDetailQueryVariables
  >(OutgoingInvoiceDetailDocument);

  private proformaFetcher = new FetchHelper<
    ProformaInvoiceDetailQuery,
    ProformaInvoiceDetailQueryVariables
  >(ProformaInvoiceDetailDocument);

  constructor(type: InvoiceTypeEnum) {
    makeObservable(this);
    this.type = type;
  }

  @computed get isLoading(): boolean {
    return (
      this.outgoingFetcher.isLoading.value ||
      this.proformaFetcher.isLoading.value
    );
  }

  @computed get isNotFound(): boolean {
    return this.hasFetched && !this.isLoading && !this.invoice;
  }

  @action.bound
  setInvoice(data: InvoiceData) {
    this.invoice = new Invoice(data);
  }

  @action.bound async fetchInvoice(id: string): Promise<void> {
    this.hasFetched = false;

    if (this.isProforma) {
      const [data, error] = await this.proformaFetcher.fetch(
        { id },
        null,
        false,
        false,
        "network-only",
      );
      this.hasFetched = true;
      if (error) return;
      this.invoice = data.outgoingProformaInvoice
        ? new Invoice(data.outgoingProformaInvoice)
        : undefined;
      return;
    }

    const [data, error] = await this.outgoingFetcher.fetch(
      { id },
      null,
      false,
      false,
      "network-only",
    );
    this.hasFetched = true;
    if (error) return;
    this.invoice = data.outgoingInvoice
      ? new Invoice(data.outgoingInvoice)
      : undefined;
  }

  @computed get isProforma() {
    return this.type === InvoiceTypeEnum.Proforma;
  }

  @computed get isOutgoing() {
    return this.type === InvoiceTypeEnum.Outgoing;
  }

  @computed get canEditPayments(): boolean {
    return (
      !!appStore.workspaceStore.settings?.enableBank &&
      can("expense_markAsPaid", "bank_read_all")
    );
  }

  @action.bound openMatchPaymentModal(): void {
    if (!this.invoice) return;
    const id = this.invoice.id;

    appStore.matchPaymentModalStore.disclosure.onOpen({
      selectedMoves: this.invoice.payments,
      document: {
        id,
        type: this.isProforma
          ? BankDocumentTypeEnum.OutgoingProformaInvoice
          : BankDocumentTypeEnum.OutgoingInvoice,
        number: this.invoice.invoice_no,
        amount: this.invoice.stats.total_amount_with_vat_in_currency,
        currency: this.invoice.currency,
        exchangeRate: this.invoice.exchange_rate,
      },
      onUpdate: async () => {
        await this.fetchInvoice(id);
      },
    });
  }

  @computed get sumInvoiceItemsVatByPercent(): InvoiceItemVatGroup[] {
    const groups = new Map<number, number>();

    this.invoice?.invoiceItems.forEach((item) => {
      const vatPercent = Number(item.vat_percent_informative ?? 0);
      const vatAmount = new PercentModel(vatPercent).percentOf(
        item.amount_in_currency,
      );
      groups.set(vatPercent, (groups.get(vatPercent) ?? 0) + vatAmount);
    });

    const vats: InvoiceItemVatGroup[] = [];

    for (const [vatPercent, vatAmount] of groups.entries()) {
      vats.push({
        vatPercent,
        vatAmount,
        vatPercentFormatted: percent.format(vatPercent),
      });
    }

    return vats.toSorted((a, b) => a.vatPercent - b.vatPercent);
  }

  @computed get sumInvoiceItemsVat(): number {
    return (
      this.invoice?.invoiceItems.reduce((prev, item) => {
        const vatPercent = Number(item.vat_percent_informative ?? 0);
        return (
          prev + new PercentModel(vatPercent).percentOf(item.amount_in_currency)
        );
      }, 0) ?? 0
    );
  }

  @computed
  get getModelLockType() {
    switch (this.type) {
      case InvoiceTypeEnum.Outgoing:
        return ModelLockLockableTypeEnum.OutgoingInvoice;
      case InvoiceTypeEnum.Proforma:
        return ModelLockLockableTypeEnum.OutgoingProformaInvoice;
      default:
        return null;
    }
  }

  @computed get hasModelLock(): boolean {
    return this.invoice?.hasModelLock ?? false;
  }

  @computed get isLocking(): boolean {
    if (!this.getModelLockType) {
      return false;
    }

    return this.invoice
      ? appStore.modelLock.isLocking(this.invoice.id, this.getModelLockType)
      : false;
  }

  @computed get isUnlocking(): boolean {
    if (!this.getModelLockType) {
      return false;
    }

    return this.invoice
      ? appStore.modelLock.isUnlocking(this.invoice.id, this.getModelLockType)
      : false;
  }

  @computed get isLockToggling(): boolean {
    return this.isLocking || this.isUnlocking;
  }

  @action.bound async lock(): Promise<boolean> {
    if (!this.invoice?.id || !this.getModelLockType) return false;

    const [data, error] = await appStore.modelLock.lockModel(
      this.invoice.id,
      this.getModelLockType,
    );

    if (error || !data?.lockModel) return false;

    appStore.UIStore.toast({
      status: "success",
      title: this.isProforma
        ? t`Proforma invoice locked successfully`
        : t`Outgoing invoice locked successfully`,
    });

    this.invoice.modelLock = data.lockModel;

    return true;
  }

  @action.bound async unlock(): Promise<boolean> {
    if (!this.invoice?.id || !this.getModelLockType) return false;

    const [data, error] = await appStore.modelLock.unlockModel(
      this.invoice.id,
      this.getModelLockType,
    );

    if (error || !data?.unlockModel) return false;

    appStore.UIStore.toast({
      status: "success",
      title: this.isProforma
        ? t`Proforma invoice unlocked successfully`
        : t`Outgoing invoice unlocked successfully`,
    });

    this.invoice.modelLock = null;

    return true;
  }
}
